import React, { createContext, useContext, useEffect, useState } from 'react';
import type {
  AuthContextType,
  LoginResult,
  User,
  VerifyMFAParams,
  VerifyMFAResult,
  SetupMFAResult,
  ActivateMFAParams,
  ActivateMFAResult,
  ChangePasswordParams,
  ChangePasswordResult,
} from 'types/auth';
import { buildApiUrl } from '../config/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const jsonFetch = (path: string, options?: RequestInit) => {
  const headers = {
    'Content-Type': 'application/json',
    ...(options?.headers ?? {}),
  };
  return fetch(buildApiUrl(path), {
    credentials: 'include',
    ...options,
    headers,
  });
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await jsonFetch('/auth/me');

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else if (response.status === 401) {
        setUser(null);
      } else {
        console.error('Auth check failed with status:', response.status);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await jsonFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.mfa_required && data.mfa) {
          return {
            success: false,
            mfaRequired: true,
            mfa: {
              factorId: data.mfa.factorId,
              challengeId: data.mfa.challengeId,
            },
          };
        }

        if (data.mfa_setup_required) {
          return {
            success: false,
            mfaSetupRequired: true,
            mfa_setup: data.mfa_setup,
          };
        }

        setUser(data.user);
        return { success: true };
      }

      return { success: false, error: data.message || 'Login failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const verifyMFA = async ({
    factorId,
    challengeId,
    code,
  }: VerifyMFAParams): Promise<VerifyMFAResult> => {
    try {
      const response = await jsonFetch('/auth/mfa/verify', {
        method: 'POST',
        body: JSON.stringify({ factorId, challengeId, code }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        return { success: true };
      }

      return { success: false, error: data.message || 'MFA verification failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const activateMFA = async ({ factorId, code }: ActivateMFAParams): Promise<ActivateMFAResult> => {
    try {
      const response = await jsonFetch('/auth/mfa/activate', {
        method: 'POST',
        body: JSON.stringify({ factorId, code }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        return { success: true };
      }

      return { success: false, error: data.message || 'MFA activation failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const setupMFA = async (): Promise<SetupMFAResult> => {
    try {
      const response = await jsonFetch('/auth/mfa/setup', {
        method: 'POST',
      });

      const data = await response.json();

      if (response.ok) {
        return {
          success: true,
          factorId: data.factorId,
          qr_code: data.qr_code,
          secret: data.secret,
          uri: data.uri,
        };
      }

      return { success: false, error: data.message || 'MFA setup failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = async () => {
    try {
      await jsonFetch('/auth/logout', {
        method: 'POST',
      });
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const changePassword = async ({
    currentPassword,
    newPassword,
  }: ChangePasswordParams): Promise<ChangePasswordResult> => {
    try {
      const response = await jsonFetch('/auth/password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true };
      }

      return { success: false, error: data.message || 'Password update failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, login, verifyMFA, activateMFA, setupMFA, logout, changePassword, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
